import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, MapPin, Luggage, Check, X as XIcon, Sparkles,
  Compass, MoreVertical, Trash2, Briefcase, Palmtree, BedDouble,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { SectionRibbon } from "@/components/AppShell";
import CoverPhotoUploader from "@/components/CoverPhotoUploader";
import PoweredByHotelbeds from "@/components/hotels/PoweredByHotelbeds";

/**
 * Phase 2 v1 — Booked trips redesigned to match the "premium travel
 * scrapbook" mockup. Each trip renders as a dual-split card:
 *   • left  = navy panel with the trip name + date range
 *   • right = trip hero image + floating gold-ringed "N days" pill
 *
 * The `NewTripDialog` and every /api/booked-trips endpoint used here
 * are unchanged — this file is a pure visual refresh.
 */

const TIDS = {
  page: "booked-trips-page",
  newBtn: "booked-trips-new-btn",
  trip: (id) => `booked-trip-card-${id}`,
  empty: "booked-trips-empty",
};

// Fallback hero used when a trip has no `image` field yet. Same tropical
// aesthetic used across TravelBio so the /booked list stays cohesive.
const FALLBACK_TRIP_IMG =
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1600&q=80";

function fmtDateRange(startIso, endIso) {
  if (!startIso) return "Dates coming soon";
  const s = new Date(startIso);
  if (isNaN(s)) return "Dates coming soon";
  const yr = s.getFullYear();
  const startStr = s.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (!endIso) return `${startStr}, ${yr}`;
  const e = new Date(endIso);
  if (isNaN(e)) return `${startStr}, ${yr}`;
  const endStr = e.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${startStr} - ${endStr}, ${e.getFullYear()}`;
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

// Helper to persist a resolved image URL (either uploaded to
// /api/uploads/image OR pasted by the user) onto a booked trip. Called
// by the CoverPhotoUploader once it has a URL in hand.
async function saveCoverUrl(tripId, url, onDone) {
  try {
    await api.put(`/booked-trips/${tripId}`, { image: url });
    onDone && onDone();
  } catch (e) {
    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save cover");
  }
}

/* -------------------------------------------------------------------- */
/*  Trip card — dual-split (navy left + hero image right + days pill)    */
/* -------------------------------------------------------------------- */
function TripHeroCard({ trip, onClick, onCoverUpdated, onDelete, onMove }) {
  const days = daysUntil(trip.start_date);
  const hasImg = !!(trip.image && trip.image.trim());
  const pillLabel =
    days == null
      ? null
      : days > 0
        ? `${days} ${days === 1 ? "day" : "days"}`
        : days === 0
          ? "today"
          : "on the road";

  return (
    <div className="relative">
      <button
        data-testid={TIDS.trip(trip.id)}
        onClick={onClick}
        className="w-full text-left rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition tb-tap grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
      >
      {/* Left navy panel */}
      <div className="bg-tb-navy-deep p-5 md:p-6 flex flex-col justify-center">
        <h3
          className="font-serif-it italic text-tb-cream leading-tight text-2xl md:text-3xl"
          data-testid={`trip-card-name-${trip.id}`}
        >
          {trip.name}
        </h3>
        <p className="mt-2 font-hand text-tb-cream/75 text-sm md:text-base">
          {fmtDateRange(trip.start_date, trip.end_date)}
        </p>
        {(trip.destination_name || trip.country) && (
          <p className="mt-1 font-hand text-tb-cream/55 text-xs md:text-sm flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {[trip.destination_name, trip.country].filter(Boolean).join(", ")}
          </p>
        )}
      </div>

      {/* Right side: hero image if present, else cream "Add cover photo" */}
      <div className="relative min-h-[140px] md:min-h-[180px]">
        {hasImg ? (
          <img
            src={trip.image}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-0"
          >
            <CoverPhotoUploader
              testId={`trip-card-add-cover-${trip.id}`}
              variant="list"
              onUploaded={(url) => saveCoverUrl(trip.id, url, onCoverUpdated)}
            />
          </div>
        )}
        {pillLabel && (
          <span
            data-testid={`trip-card-days-pill-${trip.id}`}
            className="absolute bottom-3 right-3 bg-white/95 border-2 border-tb-gold px-3 py-1 rounded-full text-xs md:text-sm text-tb-navy font-semibold shadow-sm"
          >
            {pillLabel}
          </span>
        )}
      </div>
    </button>
    {(onDelete || onMove) && (
      <TripKebabMenu trip={trip} onDelete={onDelete} onMove={onMove} />
    )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Trip kebab menu — top-right of each card                             */
/* -------------------------------------------------------------------- */
function TripKebabMenu({ trip, onDelete, onMove }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const boxRef = React.useRef(null);
  const currentType = trip.trip_type || "pleasure";
  const nextType = currentType === "pleasure" ? "business" : "pleasure";

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("touchstart", close);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("touchstart", close);
    };
  }, [open]);

  return (
    <div ref={boxRef} className="absolute top-2 right-2 z-10" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        data-testid={`trip-card-kebab-${trip.id}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Trip actions"
        aria-expanded={open}
        className="w-9 h-9 rounded-full bg-black/45 backdrop-blur border border-white/25 text-white flex items-center justify-center tb-tap hover:bg-black/60"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div
          role="menu"
          data-testid={`trip-card-menu-${trip.id}`}
          className="absolute top-11 right-0 min-w-[220px] rounded-xl bg-tb-cream border-2 border-tb-cream-3 shadow-xl overflow-hidden"
        >
          <button
            type="button"
            role="menuitem"
            data-testid={`trip-card-menu-move-${trip.id}`}
            onClick={() => { setOpen(false); onMove && onMove(trip.id, nextType); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-tb-navy hover:bg-tb-cream-2 tb-tap"
          >
            {nextType === "business"
              ? <Briefcase className="w-4 h-4 text-tb-navy" />
              : <Palmtree className="w-4 h-4 text-tb-navy" />}
            Move to {nextType === "business" ? "Business" : "Pleasure"}
          </button>
          <button
            type="button"
            role="menuitem"
            data-testid={`trip-card-menu-delete-${trip.id}`}
            onClick={() => { setOpen(false); setConfirming(true); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-tb-coral hover:bg-tb-coral/10 border-t border-tb-cream-3 tb-tap"
          >
            <Trash2 className="w-4 h-4" />
            Delete trip
          </button>
        </div>
      )}
      {confirming && (
        <div
          className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            role="dialog"
            data-testid={`trip-delete-confirm-${trip.id}`}
            className="bg-white rounded-2xl border-2 border-tb-cream-3 shadow-2xl w-full max-w-sm p-5"
          >
            <h3 className="font-serif-it italic text-tb-navy text-2xl">Delete this trip?</h3>
            <p className="mt-2 font-hand text-tb-navy/70 text-sm">
              "{trip.name}" will be permanently removed. This can't be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="px-4 h-10 rounded-full border-2 border-tb-navy/30 bg-white text-tb-navy font-hand text-sm tb-tap"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid={`trip-delete-confirm-yes-${trip.id}`}
                onClick={() => { setConfirming(false); onDelete && onDelete(trip.id); }}
                className="px-4 h-10 rounded-full bg-tb-coral text-white font-hand text-sm tb-tap"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  New trip dialog (unchanged API)                                      */
/* -------------------------------------------------------------------- */
// Exported for reuse on ChapterYearPage's footer "Add a booked trip"
// button (Phase 1 polish A3, Phase 2 merge). Same dialog, same POST
// endpoint, same idempotency guarantees.
export function NewTripDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", destination_name: "",
    start_date: "", end_date: "", total_budget: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Give your trip a name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/booked-trips", {
        ...form,
        total_budget: parseFloat(form.total_budget) || 0,
      });
      // 2026-07-15 #polish2 — removed "Trip created" toast; trip
      // appears in the list on onCreated().
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't create");
    } finally { setBusy(false); }
  };
  const inp = "w-full px-3 py-2 rounded-lg bg-tb-cream border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm";
  return (
    <div className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3">
      <form onSubmit={submit} role="dialog" aria-modal="true"
        className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-md w-full">
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span className="font-hand text-xl">new booked trip</span>
          <button type="button" onClick={onClose} className="font-hand text-tb-gold tb-tap">close</button>
        </div>
        <div className="p-4 space-y-3">
          <input className={inp} placeholder="Trip name (e.g. Honeymoon in Italy)"
            value={form.name} onChange={(e) => set("name", e.target.value)} required />
          <input className={inp} placeholder="Location (e.g. Amalfi Coast, Italy)" value={form.destination_name}
            onChange={(e) => set("destination_name", e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="font-hand text-tb-navy/65 text-xs ml-1">start date</span>
              <input type="date" className={inp} value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)} />
            </label>
            <label className="block">
              <span className="font-hand text-tb-navy/65 text-xs ml-1">end date</span>
              <input type="date" className={inp} value={form.end_date}
                min={form.start_date || undefined}
                onChange={(e) => set("end_date", e.target.value)} />
            </label>
          </div>
          <label className="block">
            <span className="font-hand text-tb-navy/65 text-xs ml-1">total budget (USD)</span>
            <input className={inp} placeholder="2000" inputMode="decimal"
              value={form.total_budget}
              onChange={(e) => set("total_budget", e.target.value.replace(/[^\d.]/g, ""))} />
          </label>
          <button disabled={busy}
            className="w-full py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap disabled:opacity-50">
            {busy ? "creating\u2026" : "create trip"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Page                                                                 */
/* -------------------------------------------------------------------- */
export default function BookedTripsPage() {
  const nav = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [hotelbedsBookings, setHotelbedsBookings] = useState([]);
  // Business/Pleasure toggle — defaults to "pleasure" (matches the
  // backend default on the trip model). Restored 2026-07-15.
  const [typeFilter, setTypeFilter] = useState("pleasure");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [trR, hbR] = await Promise.all([
        api.get("/booked-trips"),
        api.get("/hotels/bookings/mine").catch(() => ({ data: { bookings: [] } })),
      ]);
      setTrips(trR.data.trips || []);
      setHotelbedsBookings(hbR.data.bookings || []);
    } catch (err) {
      console.error("[BookedTrips] load failed:", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmTrip = async (id) => {
    try {
      const { data } = await api.post(`/booked-trips/${id}/confirm`);
      setTrips((prev) => prev.map((t) => (t.id === id ? { ...t, ...data, state: data.state || "confirmed" } : t)));
      toast.success("Trip confirmed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't confirm trip");
    }
  };

  const rejectTrip = async (id) => {
    try {
      await api.delete(`/booked-trips/${id}`);
      setTrips((prev) => prev.filter((t) => t.id !== id));
      // 2026-07-15 #polish2 — removed "Trip removed" toast; trip
      // disappears from the list.
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove trip");
    }
  };

  // Kebab-menu: move between Business and Pleasure folders.
  // Uses the existing `PATCH /api/booked-trips/{id}/move` endpoint —
  // it already handles the chapter-folder side effects (drops the
  // trip from the year journal on move-to-business, restores it on
  // move-to-pleasure).
  const moveTrip = async (id, nextType) => {
    try {
      const { data } = await api.patch(`/booked-trips/${id}/move`, { trip_type: nextType });
      const patched = data.trip || data;
      setTrips((prev) => prev.map((t) => (t.id === id ? { ...t, ...patched, trip_type: nextType } : t)));
      // 2026-07-15 #polish2 — removed "Moved to X" toast; the trip
      // visibly moves to the other folder tab.
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't move trip");
    }
  };

  const filteredTrips = trips.filter((t) => (t.trip_type || "pleasure") === typeFilter);
  const businessCount = trips.filter((t) => (t.trip_type || "pleasure") === "business").length;
  const pleasureCount = trips.length - businessCount;

  return (
    <div data-testid={TIDS.page} className="flex flex-col bg-white">
      <SectionRibbon>your booked trips</SectionRibbon>

      <div className="px-6 md:px-10 lg:px-12 pt-5 pb-3 flex items-center justify-between gap-3">
        <button
          onClick={() => nav(-1)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 font-hand text-tb-navy/70 tb-tap text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> back
        </button>
        <button
          data-testid={TIDS.newBtn}
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-tb-navy-deep text-tb-gold-bright border-2 border-tb-gold font-hand text-base tb-tap"
        >
          <Plus className="w-4 h-4" /> new trip
        </button>
      </div>

      <div className="px-6 md:px-10 lg:px-12 pb-10">
        <h1
          data-testid="booked-trips-title"
          className="font-serif-it italic text-tb-navy text-3xl md:text-4xl leading-tight"
        >
          My booked trips
        </h1>
        <p className="mt-1 font-hand text-tb-navy/55 text-sm md:text-base italic">
          Every trip you've booked, all in one place.
        </p>

        {/* Business / Pleasure toggle — restored 2026-07-15. Filters
            the list below on `trip.trip_type`. Legacy trips without
            the field are treated as "pleasure" (backend default). */}
        <div
          role="tablist"
          aria-label="Filter trips by type"
          data-testid="booked-trips-type-toggle"
          className="mt-4 inline-flex items-center gap-1 p-1 rounded-full bg-tb-cream border-2 border-tb-cream-3"
        >
          {[
            { id: "pleasure", label: "Pleasure", Icon: Palmtree,  count: pleasureCount },
            { id: "business", label: "Business", Icon: Briefcase, count: businessCount },
          ].map(({ id, label, Icon, count }) => {
            const active = typeFilter === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`booked-trips-toggle-${id}`}
                onClick={() => setTypeFilter(id)}
                className={
                  "inline-flex items-center gap-1.5 px-4 h-9 rounded-full font-hand text-sm tb-tap transition " +
                  (active
                    ? "bg-tb-navy text-tb-gold-bright border-2 border-tb-gold"
                    : "bg-transparent text-tb-navy/70 border-2 border-transparent hover:text-tb-navy")
                }
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                <span className={"ml-0.5 rounded-full px-1.5 text-[10px] " + (active ? "bg-tb-gold-bright text-tb-navy-deep" : "bg-tb-navy/10 text-tb-navy/60")}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {hotelbedsBookings.length > 0 && (
            <section data-testid="booked-trips-hotelbeds" className="mb-8">
              <h2 className="italic text-tb-navy text-2xl" style={{ fontFamily: '"Playfair Display", serif' }}>
                Hotel bookings
              </h2>
              <ul className="mt-3 space-y-3">
                {hotelbedsBookings.map((b) => (
                  <li key={b.hotelbeds_reference}>
                    <button
                      type="button"
                      data-testid={`booked-trips-hotelbeds-card-${b.hotelbeds_reference}`}
                      onClick={() => nav(`/hotels/booking/${encodeURIComponent(b.hotelbeds_reference)}`)}
                      className="w-full text-left rounded-2xl border-2 border-tb-cream-3 bg-white hover:border-tb-navy shadow-sm overflow-hidden transition p-4 flex items-center gap-3"
                    >
                      <div className="w-12 h-12 rounded-full bg-tb-navy-deep flex items-center justify-center shrink-0">
                        <BedDouble className="w-6 h-6 text-tb-gold-bright" strokeWidth={1.6} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-serif text-tb-navy text-lg truncate" style={{ fontFamily: '"Playfair Display", serif' }}>
                          {b.hotel_name}
                        </div>
                        <div className="font-hand text-tb-navy/60 text-xs">
                          {b.check_in} → {b.check_out} · {b.total_price} {b.currency}
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <PoweredByHotelbeds size="xs" />
                          <span className="font-hand text-tb-navy/45 text-[10px] uppercase tracking-widest">
                            Ref {b.hotelbeds_reference}
                          </span>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {loading ? (
            <p className="font-hand text-tb-navy/55 text-center py-10">~ loading ~</p>
          ) : filteredTrips.length === 0 ? (
            <div
              data-testid={TIDS.empty}
              className="rounded-2xl bg-tb-cream border-2 border-tb-cream-3 text-center py-14 px-6"
            >
              <div className="mx-auto w-16 h-16 rounded-full bg-tb-navy-deep flex items-center justify-center">
                <Luggage className="w-8 h-8 text-tb-gold-bright" strokeWidth={1.8} />
              </div>
              <p className="mt-4 font-serif-it italic text-tb-navy text-2xl md:text-3xl">
                {trips.length === 0
                  ? "No booked trips yet"
                  : `No ${typeFilter === "business" ? "business" : "pleasure"} trips`}
              </p>
              <p className="mt-2 font-hand text-tb-navy/60 text-sm md:text-base max-w-md mx-auto">
                When you book from Explore, your trips will appear here so you
                can plan the days, pack the bags, and track every expense.
              </p>
              <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                <button
                  data-testid="booked-trips-empty-explore-btn"
                  onClick={() => nav("/explore")}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-tb-navy-deep text-tb-gold-bright border-2 border-tb-gold font-hand text-base tb-tap hover:brightness-110"
                >
                  <Compass className="w-4 h-4" /> Go to Explore
                </button>
                <button
                  onClick={() => setShowNew(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border-2 border-tb-cream-3 hover:border-tb-gold text-tb-navy font-hand text-base tb-tap"
                >
                  <Plus className="w-4 h-4" /> Log a trip
                </button>
              </div>
            </div>
          ) : (
            <ul className="space-y-4 md:space-y-5">
              {filteredTrips.map((t) => (
                <li key={t.id}>
                  <TripHeroCard
                    trip={t}
                    onClick={() => nav(`/booked/${t.id}`)}
                    onCoverUpdated={load}
                    onDelete={rejectTrip}
                    onMove={moveTrip}
                  />
                  {t.source === "stay22_auto" && (
                    <div
                      data-testid={`stay22-auto-chip-${t.id}`}
                      className="mt-2 rounded-xl border border-tb-navy/15 bg-tb-navy/[0.03] px-3 py-2 inline-flex items-center gap-2"
                    >
                      <Sparkles className="w-3 h-3 text-tb-navy/60" />
                      <span className="font-hand text-tb-navy/70 text-xs">
                        Auto-imported from your booking
                        {t.provider && (
                          <span className="ml-1 text-tb-navy/50">· {t.provider}</span>
                        )}
                      </span>
                    </div>
                  )}
                  {t.state === "awaiting_confirmation" && (
                    <div
                      data-testid={`pending-confirm-chip-${t.id}`}
                      className="mt-2 rounded-xl border-2 border-tb-gold bg-tb-gold/10 px-3 py-2.5 flex items-center gap-3"
                    >
                      <div className="flex-1 flex flex-col">
                        <span className="font-hand text-tb-navy text-sm">
                          <Sparkles className="w-3.5 h-3.5 inline mr-1 text-tb-gold" />
                          Did you book this?
                        </span>
                        <span
                          data-testid={`pending-confirm-expiry-${t.id}`}
                          className="mt-0.5 font-hand text-tb-navy/50 text-[11px] leading-tight"
                        >
                          Will expire in 72 hrs with no response
                        </span>
                      </div>
                      <button
                        data-testid={`pending-confirm-yes-${t.id}`}
                        onClick={(e) => { e.stopPropagation(); confirmTrip(t.id); }}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm border-2 border-tb-gold tb-tap"
                      >
                        <Check className="w-3.5 h-3.5" /> Yes
                      </button>
                      <button
                        data-testid={`pending-confirm-no-${t.id}`}
                        onClick={(e) => { e.stopPropagation(); rejectTrip(t.id); }}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-tb-cream-2 text-tb-navy/70 font-hand text-sm border-2 border-tb-cream-3 tb-tap hover:border-red-300 hover:text-red-600"
                      >
                        <XIcon className="w-3.5 h-3.5" /> No
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showNew && (
        <NewTripDialog
          onClose={() => setShowNew(false)}
          onCreated={(t) => { setShowNew(false); nav(`/booked/${t.id}`); }}
        />
      )}
    </div>
  );
}
